import React from 'react';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * NewItemTag - Small "NEW" tag for recently created items (within 6 hours)
 * Usage: <NewItemTag entityType="Customer" />
 */
const NewItemTag = ({ 
  entityType = null, 
  className = '',
  showZero = false 
}) => {
  const { getNewItemCount } = useNotifications();

  // Get count of recently created items
  const count = getNewItemCount(entityType);

  // Don't show tag if count is 0 and showZero is false
  if (count === 0 && !showZero) {
    return null;
  }

  return (
    <span 
      className={`
        absolute -top-1 -left-1 
        inline-flex items-center justify-center 
        px-1.5 py-0.5
        text-xs font-bold text-white 
        bg-green-500 
        rounded-sm
        border border-white
        z-10
        ${className}
      `}
      aria-label={`${count} new items created in the last 6 hours`}
      title={`${count} new ${entityType || 'items'} created in the last 6 hours`}
    >
      NEW
    </span>
  );
};

/**
 * InlineNewItemTag - Larger "NEW" tag for use in content areas
 */
export const InlineNewItemTag = ({ 
  entityType = null, 
  className = '',
  showZero = false,
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const { getNewItemCount } = useNotifications();

  const count = getNewItemCount(entityType);

  if (count === 0 && !showZero) {
    return null;
  }

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  return (
    <span 
      className={`
        inline-flex items-center justify-center 
        ${sizeClasses[size]}
        font-bold text-white 
        bg-green-500 
        rounded-md
        border border-green-600
        ${className}
      `}
      aria-label={`${count} new items created in the last 6 hours`}
      title={`${count} new ${entityType || 'items'} created in the last 6 hours`}
    >
      NEW
    </span>
  );
};

/**
 * NewItemBadge - Combination of notification badge and NEW tag
 */
export const NewItemBadge = ({ 
  entityType = null,
  className = '',
  showNotificationCount = true,
  showNewTag = true
}) => {
  const { getEntityCount, getNewItemCount } = useNotifications();
  
  const notificationCount = getEntityCount(entityType);
  const newItemCount = getNewItemCount(entityType);
  
  const hasNotifications = notificationCount > 0;
  const hasNewItems = newItemCount > 0;
  
  if (!hasNotifications && !hasNewItems) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Notification Badge */}
      {showNotificationCount && hasNotifications && (
        <span 
          className="absolute -bottom-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white z-10"
          aria-label={`${notificationCount} unread notifications`}
        >
          {notificationCount > 99 ? '99+' : notificationCount}
        </span>
      )}
      
      {/* NEW Tag */}
      {showNewTag && hasNewItems && (
        <span 
          className="absolute -top-1 -left-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-green-500 rounded-sm border border-white z-10"
          aria-label={`${newItemCount} new items created in the last 6 hours`}
        >
          NEW
        </span>
      )}
    </div>
  );
};

export default NewItemTag;